import { useState } from 'react';

import { InfoModal, LabeledFilterSelect } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { RankChips } from '@backrs_ui/grow-ui/src/components/MyColleges/RankChips';
import { StepSlider } from '@backrs_ui/grow-ui/src/components/MyColleges/StepSlider';
import {
  COMPARE_FACTORS,
  CompareFactor,
  ComparisonSettings,
  DISTANCE_OPTIONS,
  PREFERENCE_OPTIONS,
  PreferenceFactor,
} from '@backrs_ui/grow-ui/src/redux/userColleges/userColleges.types';

import { Box, Button, SxProps, Theme, Typography } from '@mui/material';

import { ChoiceChips } from './ChoiceChips';
import { factorLabel, rankedOptions, withRanking } from '../compare.util';

const STRINGS = appStrings.modules.myColleges.compare;
const DISTANCE_CHOICES = DISTANCE_OPTIONS.map((value) => ({
  value,
  label: STRINGS.weights.distanceOptions[value],
}));
const FACTOR_OPTIONS = COMPARE_FACTORS.map((factor) => ({
  value: factor,
  label: factorLabel(factor),
}));

const isPreferenceFactor = (factor: CompareFactor): factor is PreferenceFactor =>
  factor in PREFERENCE_OPTIONS;

type CompareWeightsProps = Readonly<{
  initialFactor: CompareFactor | null;
  settings: ComparisonSettings;
  hasHome: boolean;
  onClose: () => void;
  onChange: (settings: ComparisonSettings) => void;
}>;

export const CompareWeights = ({
  initialFactor,
  settings,
  hasHome,
  onClose,
  onChange,
}: CompareWeightsProps) => {
  const [factor, setFactor] = useState(initialFactor);
  const [draft, setDraft] = useState(settings);

  return (
    <InfoModal
      open
      onClose={onClose}
      width="xs"
      featuredIcon={{ iconName: 'Scales01', size: 'lg', color: 'Colors/Foreground/fg-secondary' }}
      headerContent={
        <ModalHeading title={STRINGS.weights.title} subtitle={STRINGS.weights.subtitle} />
      }
      bodyContainerSx={bodyContainerSx}
      bodyContent={
        <Box id="compare-weights-modal" sx={bodySx}>
          <LabeledFilterSelect
            id="compare-weights-factor"
            label={STRINGS.weights.factorLabel}
            iconName="CursorBox"
            placeholder={STRINGS.weights.factorPlaceholder}
            options={FACTOR_OPTIONS}
            value={factor ?? ''}
            onChange={(value) => setFactor(value === '' ? null : (value as CompareFactor))}
          />
          {factor && (
            <FactorEditor factor={factor} draft={draft} hasHome={hasHome} onChange={setDraft} />
          )}
          <Box sx={actionsSx}>
            <Button
              id="compare-weights-cancel"
              hierarchy="Secondary gray"
              size="lg"
              fullWidth
              onClick={onClose}
            >
              {STRINGS.cancel}
            </Button>
            <Button
              id="compare-weights-confirm"
              hierarchy="Primary"
              size="lg"
              fullWidth
              onClick={() => {
                if (draft !== settings) onChange(draft);

                onClose();
              }}
            >
              {STRINGS.confirm}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

type FactorEditorProps = Readonly<{
  factor: CompareFactor;
  draft: ComparisonSettings;
  hasHome: boolean;
  onChange: (settings: ComparisonSettings) => void;
}>;

const FactorEditor = ({ factor, draft, hasHome, onChange }: FactorEditorProps) => {
  const label = factorLabel(factor);
  const ranked = isPreferenceFactor(factor);

  return (
    <>
      <Typography variant="text-sm-regular" sx={mutedSx}>
        {[STRINGS.factors[factor].explanation, ranked && STRINGS.weights.rankNote]
          .filter(Boolean)
          .join(' ')}
      </Typography>
      {ranked && (
        <RankChips
          id={`compare-weights-${factor}`}
          label={label}
          order={rankedOptions(draft, factor)}
          optionLabel={(option) => STRINGS.optionLabels[option]}
          onReorder={(order) => onChange(withRanking(draft, factor, order))}
        />
      )}
      {factor === 'distance' && (
        <Box sx={sectionSx}>
          <Typography variant="text-sm-medium" sx={sectionLabelSx}>
            {STRINGS.weights.distanceQuestion}
          </Typography>
          <ChoiceChips
            id="compare-weights-distance-option"
            label={STRINGS.weights.distanceQuestion}
            options={DISTANCE_CHOICES}
            value={draft.distance}
            onChange={(distance) => onChange({ ...draft, distance })}
          />
          {!hasHome && (
            <Typography variant="text-xs-regular" sx={mutedSx}>
              {STRINGS.weights.noZip}
            </Typography>
          )}
        </Box>
      )}
      <Box sx={sectionSx}>
        <Typography variant="text-sm-medium" sx={sectionLabelSx}>
          {STRINGS.weights.weightLabel(label)}
        </Typography>
        <StepSlider
          id={`compare-weights-${factor}-importance`}
          label={STRINGS.weights.importanceOf(label)}
          steps={STRINGS.importanceLabels}
          value={draft.importances[factor]}
          onChange={(importance) =>
            onChange({ ...draft, importances: { ...draft.importances, [factor]: importance } })
          }
        />
      </Box>
    </>
  );
};

type ModalHeadingProps = Readonly<{ title: string; subtitle: string }>;

const ModalHeading = ({ title, subtitle }: ModalHeadingProps) => (
  <Box sx={headingSx}>
    <Typography variant="text-lg-semibold" component="h2">
      {title}
    </Typography>
    <Typography variant="text-sm-regular" sx={mutedSx}>
      {subtitle}
    </Typography>
  </Box>
);

const bodyContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  zIndex: 1,
  px: backrs.spacing['spacing-3xl'],
  pb: backrs.spacing['spacing-3xl'],
});

const headingSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const bodySx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
});

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-sm'],
});

const sectionLabelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const actionsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
});

const mutedSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});
